import React, { createContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from '../../axios';
import {
    BAD_GATEWAY,
    GATEWAY_TIMEOUT,
    SERVICE_STATE,
    SERVICE_UNAVAILABLE,
    AXIOS_NETWORK_ERROR_MESSAGE,
    ADMIN_API
} from '../consts';
import PropTypes from 'prop-types';

const BEEHERO_DOMAIN = new RegExp('^https?://[a-zA-Z\\-_]*.beehero.io', 'i');
const LOCALHOST_DOMAIN = new RegExp('^https?://localhost', 'i');

export const setRefreshTimeout = () => {
    setTimeout(() => {
        window.location.reload();
    }, 60 * 1000);
};

export const AuthContext = createContext();

export const AuthProvider = (props) => {
    const [AuthState, setAuthState] = useState({
        isLoading: false,
        userInfo: null,
        error: null,
        started: false,
        serviceState: SERVICE_STATE.OK,
        refreshAuth: () => refreshAuth(),
        logout: () => logout()
    });

    const setServiceState = (newState) => {
        setAuthState((latestAuthState) => {
            return { ...latestAuthState, serviceState: newState, isLoading: false, error: null, started: true };
        });
        if (newState !== SERVICE_STATE.OK) {
            setRefreshTimeout();
        }
    };

    const toggleLoading = () => {
        setAuthState((latestAuthState) => {
            return { ...latestAuthState, isLoading: true, userInfo: null, error: null, started: true };
        });
    };
    const fetchSuccess = (data) => {
        const userInfo = { ...data, rolenames: data?.rolenames.filter((role) => role?.trim()) };
        setAuthState((latestAuthState) => {
            return { ...latestAuthState, userInfo, isLoading: false, error: null, started: true };
        });
    };
    const fetchFail = (err) => {
        setAuthState((latestAuthState) => {
            return { ...latestAuthState, userInfo: null, isLoading: false, error: err, started: true };
        });
    };
    const refreshAuth = () => {
        fetchInfo();
    };

    const monitorServiceState = () => {
        if (
            process.env.REACT_APP_MAINTENANCE &&
            typeof process.env.REACT_APP_MAINTENANCE === 'string' &&
            process.env.REACT_APP_MAINTENANCE.toLowerCase() === 'true'
        ) {
            setServiceState(SERVICE_STATE.MAINTENANCE);
        }

        axios.interceptors.response.use(undefined, (error) => {
            if (error?.config?.baseURL) {
                const baseURL = error.config.baseURL;
                if (BEEHERO_DOMAIN.test(baseURL) || LOCALHOST_DOMAIN.test(baseURL)) {
                    const status = error.response?.status;
                    if (status === SERVICE_UNAVAILABLE) {
                        setServiceState(SERVICE_STATE.MAINTENANCE);
                    } else if (status === GATEWAY_TIMEOUT || status === BAD_GATEWAY) {
                        setServiceState(SERVICE_STATE.ERROR);
                    } else if (error?.message === AXIOS_NETWORK_ERROR_MESSAGE) {
                        setServiceState(SERVICE_STATE.ERROR);
                    }
                    return Promise.reject(error);
                }
            }
            return Promise.reject(error);
        });
    };

    const fetchInfo = () => {
        toggleLoading();

        axios
            .get(`${ADMIN_API}/auth`)
            .then((res) => {
                if (res.status === 200) {
                    fetchSuccess(res.data);
                } else {
                    Promise.reject(res.data);
                }
            })
            .catch((error) => {
                if (AuthState.serviceState === SERVICE_STATE.OK) {
                    fetchFail(error);
                }
            });
    };

    const logout = () => {
        toggleLoading();

        axios
            .post(`${ADMIN_API}/logout`)
            .then((res) => {
                if (res.status === 200) {
                    setAuthState({ ...AuthState, userInfo: null, isLoading: false });
                }
                refreshAuth();
            })
            .catch(() => {
                if (AuthState.serviceState === SERVICE_STATE.OK) {
                    refreshAuth();
                    toast.error('Failed to logout');
                }
            });
    };

    useEffect(monitorServiceState, []);
    useEffect(fetchInfo, []);

    return <AuthContext.Provider value={AuthState}>{props.children}</AuthContext.Provider>;
};

export default AuthProvider;

AuthProvider.propTypes = {
    children: PropTypes.node
};
